import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Team, Tournament, MATCH_CONFIG } from '@/types';
import { supabase, getTournament, getTeams, getMatchScoreboardSummary, getRalliesBySetForDisplay, getPlayerOfMatch, PlayerOfMatchWinner } from '@/lib/supabase';
import WatchLiveButton from '@/components/WatchLiveButton';

interface PublicLiveScoreboardProps {
  matchId: string;
  tournamentId: string;
}

function mapRawTeam(t: any): Team {
  return {
    id: t.id,
    name: t.name,
    shortName: t.short_name ?? t.shortName ?? '',
    color: t.color,
    logoUrl: t.logo_url ?? t.logoUrl ?? null,
    players: (t.players || []).map((p: any) => ({
      id: p.id,
      name: p.name,
      number: p.number,
      teamId: p.team_id ?? p.teamId ?? t.id,
    })),
  };
}

function TeamLogo({ team, size = 64 }: { team: Team; size?: number }) {
  const [imgError, setImgError] = useState(false);

  if (team.logoUrl && !imgError) {
    return (
      <img
        src={team.logoUrl}
        alt={team.name}
        onError={() => setImgError(true)}
        style={{ width: size, height: size }}
        className="rounded-xl object-contain bg-surface-raised p-1.5"
      />
    );
  }

  return (
    <div
      style={{ width: size, height: size, backgroundColor: team.color }}
      className="rounded-xl flex items-center justify-center shadow-md"
    >
      <span className="text-white font-bold" style={{ fontSize: size * 0.38 }}>
        {team.shortName?.charAt(0) || team.name?.charAt(0)}
      </span>
    </div>
  );
}

export default function PublicLiveScoreboard({ matchId, tournamentId }: PublicLiveScoreboardProps) {
  const navigate = useNavigate();
  const [matchData, setMatchData] = useState<any>(null);
  const [teams, setTeams] = useState<Map<string, Team>>(new Map());
  const [allPlayers, setAllPlayers] = useState<Map<string, string>>(new Map());
  const [playerTeams, setPlayerTeams] = useState<Map<string, Team>>(new Map());
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ---- Expandable Sets (accordion) ----
  // expandedSetId: id of the set currently expanded (completed or live). null = all collapsed.
  const [expandedSetId, setExpandedSetId] = useState<string | null>(null);
  // Cache of rallies fetched on-demand, keyed by setId.
  const ralliesCacheRef = useRef<Map<string, any[]>>(new Map());
  // Tracks sets currently fetching to avoid duplicate requests.
  const fetchingRef = useRef<Set<string>>(new Set());
  // Bumped when a fetch completes so the new cache entry triggers a re-render.
  const [ralliesCacheVersion, setRalliesCacheVersion] = useState(0);
  const [playerOfMatch, setPlayerOfMatch] = useState<PlayerOfMatchWinner[]>([]);

  const fetchRalliesForSet = useCallback(async (setId: string) => {
    if (ralliesCacheRef.current.has(setId) || fetchingRef.current.has(setId)) {
      return;
    }
    fetchingRef.current.add(setId);
    try {
      const rallies = await getRalliesBySetForDisplay(setId);
      ralliesCacheRef.current.set(setId, rallies || []);
      setRalliesCacheVersion((v) => v + 1);
    } catch (err) {
      console.error('Failed to load rallies for set:', setId, err);
    } finally {
      fetchingRef.current.delete(setId);
    }
  }, []);

  const toggleSetExpansion = useCallback((setId: string) => {
    setExpandedSetId((curr) => {
      if (curr === setId) return null; // collapse
      fetchRalliesForSet(setId); // lazy load on expand
      return setId; // accordion: swap to new set
    });
  }, [fetchRalliesForSet]);

  const isRalliesLoading = (setId: string) => fetchingRef.current.has(setId);
  const getCachedRallies = (setId: string): any[] | null =>
    ralliesCacheRef.current.get(setId) ?? null;

  useEffect(() => {
    const loadMatch = async () => {
      try {
        const [match, tournamentData, teamsData] = await Promise.all([
          getMatchScoreboardSummary(matchId),
          getTournament(tournamentId),
          getTeams(),
        ]);
        setMatchData(match);
        setTournament(tournamentData);

        const teamMap = new Map<string, Team>();
        (teamsData || []).forEach((t: any) => {
          const mapped = mapRawTeam(t);
          teamMap.set(mapped.id, mapped);
        });
        setTeams(teamMap);

        const playerMap = new Map<string, string>();
        const playerTeamMap = new Map<string, Team>();
        (teamsData || []).forEach((t: any) => {
          const mappedTeam = mapRawTeam(t);
          (t.players || []).forEach((p: any) => {
            playerMap.set(p.id, p.name);
            playerTeamMap.set(p.id, mappedTeam);
          });
        });
        setAllPlayers(playerMap);
        setPlayerTeams(playerTeamMap);
      } catch (err) {
        console.error('Failed to load match:', err);
      }
    };
    loadMatch();
  }, [matchId, tournamentId]);

  // Realtime subscription: fires whenever a set row for this match changes
  // (score update, set won, new set created). Refetches match summary on every event.
  // A 30s fallback poll runs in parallel to recover from dropped WS connections.
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const match = await getMatchScoreboardSummary(matchId);
        setMatchData(match);
      } catch (err) {
        console.error('[Realtime] Failed to refresh match summary:', err);
      }
    };

    const channel = supabase
      .channel(`scoreboard-sets-${matchId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sets', filter: `match_id=eq.${matchId}` },
        fetchSummary,
      )
      .subscribe();

    // Fallback poll — safety net if the realtime connection drops
    pollRef.current = setInterval(() => {
      if (!document.hidden) fetchSummary();
    }, 30000);

    return () => {
      supabase.removeChannel(channel);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [matchId]);

  // Real-time rally refresh for the CURRENT (live) set only.
  // Dep is currentSet?.id — NOT matchData — so the interval is only recreated
  // when the active set changes, not on every summary poll tick.
  const currentSetIdForPoll = useMemo(
    () => (matchData?.sets || []).find((s: any) => !s.ended_at)?.id ?? null,
    [matchData]
  );

  // Realtime subscription for rally changes on the current (live) set.
  // Dep is currentSetIdForPoll so the channel is rebuilt only when the active
  // set changes, not on every matchData update. Also does an initial fetch.
  useEffect(() => {
    if (!currentSetIdForPoll) return;
    const setId = currentSetIdForPoll;

    const fetchRallies = async () => {
      if (fetchingRef.current.has(setId)) return;
      fetchingRef.current.add(setId);
      try {
        const rallies = await getRalliesBySetForDisplay(setId);
        ralliesCacheRef.current.set(setId, rallies || []);
        setRalliesCacheVersion((v) => v + 1);
      } catch (err) {
        console.error('[Realtime] Failed to load rallies:', err);
      } finally {
        fetchingRef.current.delete(setId);
      }
    };

    fetchRallies();

    const channel = supabase
      .channel(`scoreboard-rallies-${setId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'rallies', filter: `set_id=eq.${setId}` },
        fetchRallies,
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [currentSetIdForPoll]);

  useEffect(() => {
    if (!matchData) return;

    const updateTimer = () => {
      if (matchData.start_time) {
        const start = new Date(matchData.start_time).getTime();
        const end = matchData.end_time ? new Date(matchData.end_time).getTime() : Date.now();
        const elapsed = Math.max(0, Math.floor((end - start) / 1000));
        setElapsedTime(elapsed);
      }
    };

    updateTimer();
    timerRef.current = setInterval(updateTimer, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [matchData?.start_time, matchData?.end_time]);

  const handleManualRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const match = await getMatchScoreboardSummary(matchId);
      setMatchData(match);
    } finally {
      setRefreshing(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const formatSetDuration = (start: string, end: string): string => {
    const duration = Math.floor((new Date(end).getTime() - new Date(start).getTime()) / 1000);
    const mins = Math.floor(duration / 60);
    const secs = duration % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const teamA = teams.get(matchData?.team_a_id);
  const teamB = teams.get(matchData?.team_b_id);
  const sets = matchData?.sets || [];
  const currentSet = sets.find((s: any) => !s.ended_at);
  // Current set rallies come from the on-demand cache (real-time refreshed),
  // since the lightweight summary poll no longer embeds rally rows.
  const currentSetRallies = currentSet ? (getCachedRallies(currentSet.id) || []) : [];
  // Touch cache version so this memo recomputes when a fetch completes.
  void ralliesCacheVersion;
  const completedSets = sets.filter((s: any) => s.ended_at);
  const teamASetsWon = sets.filter((s: any) => s.winner_id === matchData?.team_a_id).length;
  const teamBSetsWon = sets.filter((s: any) => s.winner_id === matchData?.team_b_id).length;
  const isMatchComplete = matchData?.status === 'match-won';
  const isMatchLive = matchData?.status === 'live' || matchData?.status === 'set-won';
  const maxSets = matchData ? MATCH_CONFIG[matchData.match_type as 'league' | 'playoff'].maxSets : 3;
  const winnerId = matchData?.winner_id;
  const winner = winnerId === matchData?.team_a_id ? teamA : winnerId === matchData?.team_b_id ? teamB : null;

  // Load Player of the Match only for completed matches (on-demand).
  useEffect(() => {
    if (!matchData?.id || matchData.status !== 'match-won') {
      setPlayerOfMatch([]);
      return;
    }
    getPlayerOfMatch(matchData.id)
      .then(winners => setPlayerOfMatch(winners))
      .catch(err => console.error('[PlayerOfMatch] failed:', err));
  }, [matchData?.id, matchData?.status]);

  const servingInfo = useMemo(() => {
    if (!currentSet) return null;

    // Use the authoritative server columns on the set record when available.
    // These are written immediately on server selection, so they're always up to date.
    if (currentSet.current_serving_team_id) {
      const servingTeam = teams.get(currentSet.current_serving_team_id);
      const serverName = currentSet.current_server_id
        ? allPlayers.get(currentSet.current_server_id) || 'Unknown'
        : 'Selecting...';
      return { team: servingTeam, serverName, teamId: currentSet.current_serving_team_id };
    }

    // Fallback: derive from last rally (for legacy matches without the new columns)
    if (currentSetRallies.length === 0) return null;

    const sortedRallies = [...currentSetRallies].sort((a: any, b: any) => a.rally_number - b.rally_number);
    const lastRally = sortedRallies[sortedRallies.length - 1];

    if (!lastRally) return null;

    const isServiceBreak = lastRally.point_won_by_id !== lastRally.serving_team_id;
    const servingTeamId = isServiceBreak ? lastRally.point_won_by_id : lastRally.serving_team_id;
    const servingTeam = teams.get(servingTeamId);
    const serverId = isServiceBreak ? null : lastRally.server_id;
    const serverName = serverId ? allPlayers.get(serverId) || 'Unknown' : 'Selecting...';

    return { team: servingTeam, serverName, teamId: servingTeamId };
  }, [currentSet, currentSetRallies, teams, allPlayers]);

  // Mirrors the Scorer View "Set Rally History" layout (RallyHistoryModal):
  // rally number | serving team (color) | server name | → | winning team (color)
  // + categories row with player names colored by their team.
  const formatCategoriesDisplay = (rally: any): React.ReactNode => {
    const cats = rally.categories;
    if (!cats || cats.length === 0) return null;

    const grouped = new Map<string, Array<{ name: string; color: string }>>();
    for (const c of cats) {
      const playerTeam = playerTeams.get(c.playerId);
      const name = allPlayers.get(c.playerId)?.split(' ')[0] ?? '?';
      const color = playerTeam?.color ?? '#64748b';
      const list = grouped.get(c.category) || [];
      list.push({ name, color });
      grouped.set(c.category, list);
    }

    const order = ['A', 'D', 'L', 'S', 'B', 'F'];
    const elements: React.ReactNode[] = [];
    for (const cat of order) {
      const entries = grouped.get(cat);
      if (!entries || entries.length === 0) continue;
      const joined = entries.map((e, i) => (
        <span key={`${cat}-${i}`} style={{ color: e.color }}>{e.name}</span>
      )).reduce<React.ReactNode[]>((acc, curr, idx) => {
        if (idx === 0) return [curr];
        return [...acc, <span key={`comma-${idx}`} className="text-text-muted">, </span>, curr];
      }, []);
      elements.push(
        <span key={cat} className="font-medium">
          <span className="text-text-muted">{cat}:</span>
          {joined}
        </span>
      );
    }
    return elements.length > 0
      ? <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px]">{elements}</div>
      : null;
  };

  // Service-break tinting matches the scorer: service break = faint red, FOUL = faint amber.
  const renderRallyRow = (rally: any, score?: { a: number; b: number }) => {
    const rallyServingTeam = teams.get(rally.serving_team_id);
    const rallyWinningTeam = teams.get(rally.point_won_by_id);
    const serverName = rally.server_id ? allPlayers.get(rally.server_id) ?? '—' : '—';
    const isServiceBreak = rally.point_won_by_id !== rally.serving_team_id;
    const categoriesDisplay = formatCategoriesDisplay(rally);
    const isServeFoul = rally.is_server_fault || rally.lost_reason === 'FOUL';

    const rowTint = isServeFoul
      ? 'bg-warning/10'
      : isServiceBreak
        ? 'bg-error/5'
        : '';

    return (
      <div key={rally.id} className={`px-4 py-2.5 text-xs ${rowTint}`}>
        <div className="flex items-center gap-2">
          <span className="w-7 text-center text-text-muted font-mono-nums">
            {rally.rally_number}
          </span>
          <span className="w-9 font-bold text-center" style={{ color: rallyServingTeam?.color ?? '#52525B' }}>
            {rallyServingTeam?.shortName ?? '—'}
          </span>
          <span className="flex-1 text-text-secondary truncate">
            {isServeFoul ? (
              <span className="text-warning font-semibold">SF: {serverName}</span>
            ) : (
              serverName
            )}
          </span>
          <span className="w-3 text-center text-text-muted">→</span>
          <span className="w-9 font-bold text-center" style={{ color: rallyWinningTeam?.color ?? '#52525B' }}>
            {rallyWinningTeam?.shortName ?? '—'}
          </span>
          {score && teamA && teamB && (
            <span className="font-mono font-bold text-[11px] ml-auto pl-2 shrink-0">
              <span style={{ color: teamA.color }}>{score.a}</span>
              <span className="text-text-muted">–</span>
              <span style={{ color: teamB.color }}>{score.b}</span>
            </span>
          )}
        </div>
        {categoriesDisplay && (
          <div className="mt-1 pl-[4.5rem]">
            {categoriesDisplay}
          </div>
        )}
      </div>
    );
  };

  // Compact rally list — sorted ascending, with running score after each rally.
  const renderRallyList = (rallies: any[]) => {
    const sorted = [...rallies].sort((a: any, b: any) => a.rally_number - b.rally_number);
    let rA = 0, rB = 0;
    return (
      <div className="divide-y divide-border">
        {sorted.map((rally: any) => {
          if (rally.point_won_by_id === matchData.team_a_id) rA++;
          else if (rally.point_won_by_id === matchData.team_b_id) rB++;
          return renderRallyRow(rally, { a: rA, b: rB });
        })}
      </div>
    );
  };

  const isSetExpanded = (setId: string) => expandedSetId === setId;

  if (!matchData || !teamA || !teamB) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="w-8 h-8 border-2 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const renderTeamPanel = (team: Team, score: number, setsWon: number, isServing: boolean, side: 'left' | 'right') => {
    const servingBg = isServing && isMatchLive
      ? (side === 'left' ? 'bg-team-a/5' : 'bg-team-b/5')
      : '';

    return (
      <div
        className={`p-4 sm:p-5 text-center transition-all ${servingBg}`}
        style={{
          ...(side === 'left' ? { borderRight: '1px solid var(--color-border, #3F3F46)' } : {}),
          ...(isServing && isMatchLive
            ? side === 'left'
              ? { borderLeft: `4px solid ${team.color}`, paddingLeft: 'calc(1rem - 4px)' }
              : { borderRight: `4px solid ${team.color}`, paddingRight: 'calc(1rem - 4px)' }
            : {}),
        }}
      >
        {/* Team Logo */}
        <div className="flex justify-center mb-2">
          <TeamLogo team={team} size={56} />
        </div>

        {/* Team Name */}
        <p className="text-base font-bold text-text-primary truncate leading-tight">{team.name}</p>
        <p className="text-sm font-semibold leading-tight" style={{ color: team.color }}>{team.shortName}</p>

        {/* Rotation Indicators (Sets Won) */}
        <div className="flex justify-center gap-1.5 mt-2">
          {[...Array(maxSets)].map((_, i) => (
            <span
              key={i}
              className={`w-2.5 h-2.5 rounded-full transition-all ${i < setsWon ? 'scale-110 shadow-sm' : ''}`}
              style={{ backgroundColor: i < setsWon ? team.color : '#3F3F46' }}
            />
          ))}
        </div>

        {/* Score — largest element */}
        <div className="font-mono-nums text-5xl sm:text-6xl font-bold text-text-primary mt-2 leading-none">
          {score}
        </div>

        {/* Serving Badge + Server Name — only for serving team, fixed-height to keep panels aligned */}
        <div className="mt-2 flex flex-col items-center gap-1" style={{ minHeight: '2.5rem' }}>
          {isServing && isMatchLive && (
            <>
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${team.color}20`, color: team.color }}
              >
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: team.color }} />
                Serving
              </span>
              {servingInfo?.serverName && (
                <p className="text-xs text-text-muted font-medium truncate max-w-full">
                  {servingInfo.serverName}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-surface border-b border-border shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(`/live/${tournamentId}`)}
              className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span className="text-sm font-semibold">Back</span>
            </button>

            <div className="text-center flex-1 px-4">
              <p className="text-sm font-bold text-text-primary truncate">{tournament?.name}</p>
              <p className="text-xs text-text-muted font-medium">Match #{matchData.match_number}</p>
            </div>

            <button
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="p-2.5 rounded-xl bg-surface-raised border border-border hover:bg-border transition-all disabled:opacity-50"
            >
              <svg
                width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                className={`text-text-secondary ${refreshing ? 'animate-spin' : ''}`}
              >
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
          {/* Match Status Badge */}
          <div className="text-center">
            {isMatchComplete ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-success/15 text-success rounded-full">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-bold">MATCH COMPLETED</span>
              </div>
            ) : isMatchLive ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-surface border border-border text-serving rounded-full">
                <span className="w-2 h-2 rounded-full bg-serving animate-pulse" />
                <span className="text-sm font-bold">SET {currentSet?.set_number || 1} OF {maxSets}</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-surface-raised border border-border text-text-secondary rounded-full">
                <span className="text-sm font-bold">MATCH #{matchData.match_number}</span>
              </div>
            )}
          </div>

          {/* Watch Live / Watch Replay — only when a streaming URL is configured */}
          {matchData?.live_stream_url && (
            <div className="flex justify-center">
              <WatchLiveButton url={matchData.live_stream_url} isCompleted={isMatchComplete} />
            </div>
          )}

          {/* Match Details — completed matches only */}
          {isMatchComplete && teamA && teamB && (
            <div className="rounded-xl p-4 bg-surface border border-border text-center">
              <p className="text-xs text-text-muted font-medium">
                Match #{matchData.match_number}{matchData.date ? ` · ${new Date(matchData.date + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
              </p>
              <p className="text-sm font-bold text-text-primary mt-1">
                <span style={{ color: teamA.color }}>{teamA.name}</span>
                <span className="text-text-muted"> vs </span>
                <span style={{ color: teamB.color }}>{teamB.name}</span>
              </p>
            </div>
          )}

          {/* Match Winner — prominent, scorer-style (completed matches only) */}
          {isMatchComplete && winner && (
            <div
              className="rounded-xl p-8 mb-5 text-center"
              style={{ backgroundColor: `${winner.color}15`, borderColor: winner.color, borderWidth: 2 }}
            >
              <p className="text-sm text-text-muted uppercase mb-2">MATCH WON</p>
              <div className="flex justify-center mb-4">
                {winner.logoUrl ? (
                  <img
                    src={winner.logoUrl}
                    alt={`${winner.shortName} logo`}
                    className="w-[100px] h-[100px] rounded-xl object-contain bg-surface p-2"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling;
                      if (fallback) (fallback as HTMLElement).style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className={`w-[100px] h-[100px] rounded-xl flex items-center justify-center ${winner.logoUrl ? 'hidden' : ''}`}
                  style={{ backgroundColor: winner.color }}
                >
                  <span className="text-white text-4xl font-bold">{winner.shortName?.charAt(0) || '?'}</span>
                </div>
              </div>
              <p className="text-2xl font-bold mb-1" style={{ color: winner.color }}>{winner.name.toUpperCase()}</p>
              <p className="text-lg text-text-secondary">{winner.shortName}</p>
            </div>
          )}

          {/* Player of the Match — completed matches only */}
          {isMatchComplete && playerOfMatch.length > 0 && (
            <div className="card mb-5 border-2 border-warning/30 bg-warning/5">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🏆</span>
                <h2 className="text-xs font-bold text-warning uppercase tracking-wider">Player Of The Match</h2>
              </div>
              <div className="space-y-3">
                {playerOfMatch.map(p => {
                  const pomTeam = Array.from(teams.values()).find(t => t.name === p.teamName);
                  return (
                    <div key={p.playerId} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {pomTeam?.logoUrl ? (
                          <img src={pomTeam.logoUrl} alt={pomTeam.shortName} className="w-8 h-8 rounded-lg object-contain bg-surface-raised p-1" />
                        ) : pomTeam ? (
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: pomTeam.color }}>
                            <span className="text-white font-bold text-xs">{pomTeam.shortName?.charAt(0)}</span>
                          </div>
                        ) : null}
                        <div>
                          <p className="text-sm font-bold text-text-primary">{p.playerName}</p>
                          <p className="text-xs text-text-muted">{p.teamName}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          {'⭐'.repeat(Math.min(p.totalStars, 1))}
                          {p.totalStars > 3 && <span className="text-xs text-warning font-bold">×{p.totalStars}</span>}
                        </div>
                        <p className="text-[10px] text-text-muted mt-0.5">
                          {[
                            p.acerStars > 0 && `${p.acerStars}A`,
                            p.defenderStars > 0 && `${p.defenderStars}D`,
                            p.liberoStars > 0 && `${p.liberoStars}L`,
                            p.setterStars > 0 && `${p.setterStars}S`,
                            p.blockerStars > 0 && `${p.blockerStars}B`,
                            p.finisherStars > 0 && `${p.finisherStars}F`,
                          ].filter(Boolean).join(' ')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Set Scores — completed matches only */}
          {isMatchComplete && (
            <div className="card mb-5">
              <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">SET SCORES</h2>
              <div className="space-y-3">
                {sets.map((set: any, index: number) => (
                  <div key={set.id} className="flex items-center justify-between py-2">
                    <span className="text-sm text-text-secondary">Set {index + 1}</span>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold w-8" style={{ color: teamA?.color }}>{teamA?.shortName}</span>
                        <span className="font-mono-nums text-xl font-bold text-text-primary">{set.score_a}</span>
                      </div>
                      <span className="text-text-muted">—</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono-nums text-xl font-bold text-text-primary">{set.score_b}</span>
                        <span className="text-sm font-semibold w-8" style={{ color: teamB?.color }}>{teamB?.shortName}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Match Stats — completed matches only */}
          {isMatchComplete && (
            <div className="card mb-5">
              <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">MATCH STATS</h2>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-text-secondary">Sets Played</span>
                  <span className="font-mono-nums font-bold text-text-primary">{completedSets.length}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-text-secondary">Sets Won by {teamA?.shortName}</span>
                  <span className="font-mono-nums font-bold" style={{ color: teamA?.color }}>{teamASetsWon}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-text-secondary">Sets Won by {teamB?.shortName}</span>
                  <span className="font-mono-nums font-bold" style={{ color: teamB?.color }}>{teamBSetsWon}</span>
                </div>
              </div>
            </div>
          )}

          {/* Scoreboard - Team Panels (live matches only) */}
          {!isMatchComplete && (
          <div className="bg-surface border border-border rounded-2xl shadow-lg overflow-hidden">
            <div className="grid grid-cols-2">
              {renderTeamPanel(teamA, currentSet?.score_a ?? 0, teamASetsWon, servingInfo?.teamId === teamA.id, 'left')}
              {renderTeamPanel(teamB, currentSet?.score_b ?? 0, teamBSetsWon, servingInfo?.teamId === teamB.id, 'right')}
            </div>

            {/* Score Footer */}
            <div className="bg-surface-raised border-t border-border px-6 py-3">
              <div className="text-center">
                <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-0.5">Current Score</p>
                <p className="font-mono-nums text-2xl font-bold text-text-primary">
                  {currentSet?.score_a ?? 0} : {currentSet?.score_b ?? 0}
                </p>
              </div>
            </div>
          </div>
          )}

          {/* Match Summary Cards (live matches only) */}
          {!isMatchComplete && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Current Set */}
            <div className="bg-surface border border-border p-3 text-center flex flex-col justify-center" style={{ minHeight: '80px' }}>
              <p className="text-xs text-text-muted font-medium uppercase mb-1">Current Set</p>
              <p className="text-2xl font-bold text-text-primary">{currentSet?.set_number || 1}</p>
            </div>

            {/* Total Rallies */}
            <div className="bg-surface border border-border p-3 text-center flex flex-col justify-center" style={{ minHeight: '80px' }}>
              <p className="text-xs text-text-muted font-medium uppercase mb-1">Total Rallies</p>
              <p className="text-2xl font-bold text-text-primary">{currentSetRallies.length}</p>
            </div>

            {/* Serving Summary */}
            <div className="bg-surface border border-border p-3 text-center flex flex-col justify-center" style={{ minHeight: '80px' }}>
              <p className="text-xs text-text-muted font-medium uppercase mb-1">Serving</p>
              {servingInfo?.team ? (
                <>
                  <p className="text-lg font-bold leading-tight" style={{ color: servingInfo.team.color }}>
                    {servingInfo.team.shortName}
                  </p>
                  <p className="text-xs text-text-muted font-medium truncate mt-0.5">
                    {servingInfo.serverName || '—'}
                  </p>
                </>
              ) : (
                <p className="text-2xl font-bold text-text-muted">—</p>
              )}
            </div>

            {/* Sets Won */}
            <div className="bg-surface border border-border p-3 text-center flex flex-col justify-center" style={{ minHeight: '80px' }}>
              <p className="text-xs text-text-muted font-medium uppercase mb-1">Sets Won</p>
              <p className="text-2xl font-bold text-text-primary">{teamASetsWon} - {teamBSetsWon}</p>
            </div>
          </div>
          )}

          {/* Match Duration */}
          <div className="bg-surface border border-border p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${isMatchComplete ? 'bg-success' : 'bg-serving animate-pulse'}`} />
                <span className="text-sm font-bold text-text-secondary">
                  {isMatchComplete ? 'Match Duration' : 'Match Time'}
                </span>
              </div>
              <p className="font-mono-nums text-2xl font-bold text-text-primary">
                {formatDuration(elapsedTime)}
              </p>
            </div>
          </div>

          {/* Completed Sets — expandable accordion */}
          {completedSets.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-bold text-text-secondary px-1">Completed Sets</p>
              {completedSets
                .sort((a: any, b: any) => a.set_number - b.set_number)
                .map((set: any) => {
                  const setWinner = set.winner_id === matchData.team_a_id ? teamA : teamB;
                  const winnerScore = set.winner_id === matchData.team_a_id ? set.score_a : set.score_b;
                  const loserScore = set.winner_id === matchData.team_a_id ? set.score_b : set.score_a;
                  const duration = set.ended_at && set.started_at
                    ? formatSetDuration(set.started_at, set.ended_at)
                    : '—';
                  const expanded = isSetExpanded(set.id);
                  const cachedRallies = getCachedRallies(set.id);

                  return (
                    <div key={set.id} className="bg-surface border border-border overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleSetExpansion(set.id)}
                        className="w-full p-3 flex items-center justify-between text-left transition-colors hover:bg-surface-raised/80"
                        aria-expanded={expanded}
                      >
                        {/* Left: Set label + Winner info */}
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs font-bold text-text-muted uppercase tracking-wide flex-shrink-0">
                            Set {set.set_number}
                          </span>
                          <div className="flex items-center gap-2 min-w-0">
                            <TeamLogo team={setWinner!} size={32} />
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-text-primary truncate leading-tight">
                                {setWinner?.name}
                              </p>
                              <span
                                className="inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded mt-0.5"
                                style={{ backgroundColor: `${setWinner!.color}20`, color: setWinner!.color }}
                              >
                                Winner
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right: Score + Duration + Chevron */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="flex flex-col items-end">
                            <span className="font-mono-nums text-lg font-bold text-text-primary leading-tight">
                              {winnerScore} - {loserScore}
                            </span>
                            <span className="text-xs text-text-muted font-medium mt-0.5">{duration}</span>
                          </div>
                          <svg
                            className={`w-5 h-5 text-text-muted flex-shrink-0 transition-transform duration-250 ${expanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>

                      {/* Expandable rally list — grid-rows animation (no page jump) */}
                      <div
                        className="grid transition-all duration-250 ease-out"
                        style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
                      >
                        <div className="overflow-hidden">
                          {isRalliesLoading(set.id) && !cachedRallies ? (
                            <div className="flex items-center justify-center py-6">
                              <span className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
                            </div>
                          ) : cachedRallies && cachedRallies.length > 0 ? (
                            <>
                              <div className="px-4 py-1.5 bg-surface-raised border-y border-border flex items-center justify-between">
                                <span className="text-xs font-bold text-text-muted uppercase tracking-wide">Rallies</span>
                                <span className="text-xs text-text-muted font-medium">{cachedRallies.length} total</span>
                              </div>
                              {renderRallyList(cachedRallies)}
                            </>
                          ) : (
                            <p className="text-sm text-text-muted text-center py-6">No rally data recorded for this set.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {/* Current Live Set — expandable with real-time rallies */}
          {currentSet && !isMatchComplete && (
            <div className="bg-surface border border-border overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSetExpansion(currentSet.id)}
                className="w-full px-4 py-2.5 bg-surface-raised border-b border-border flex items-center justify-between text-left transition-colors hover:bg-border/50"
                aria-expanded={isSetExpanded(currentSet.id)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-error opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-error" />
                  </span>
                  <p className="text-sm font-bold text-text-primary">Current Set Rallies</p>
                  <span className="text-xs text-text-muted font-medium">Set {currentSet.set_number}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-text-secondary font-medium">{currentSetRallies.length}</span>
                  <svg
                    className={`w-5 h-5 text-text-muted transition-transform duration-250 ${isSetExpanded(currentSet.id) ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              <div
                className="grid transition-all duration-250 ease-out"
                style={{ gridTemplateRows: isSetExpanded(currentSet.id) ? '1fr' : '0fr' }}
              >
                <div className="overflow-hidden">
                  {isSetExpanded(currentSet.id) && (
                    currentSetRallies.length > 0 ? (
                      renderRallyList(currentSetRallies)
                    ) : (
                      <p className="text-sm text-text-muted text-center py-6">No rallies scored yet in this set.</p>
                    )
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Empty state — message depends on actual match state, not just current set rally count */}
          {currentSetRallies.length === 0 && !isMatchComplete && (
            <div className="bg-surface border border-border p-8 text-center">
              <div className="text-4xl mb-2">🏐</div>
              {isMatchLive ? (
                <>
                  <p className="text-sm font-bold text-text-secondary">
                    Set {currentSet?.set_number ?? 1} has started. Waiting for the first point.
                  </p>
                  <p className="text-xs text-text-muted mt-1">Rallies will appear here</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-bold text-text-secondary">Waiting for match to start...</p>
                  <p className="text-xs text-text-muted mt-1">Rallies will appear here</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
